import { ClaudiaIcon } from "@/components/claudia-icon";
import { VersionRowBadge } from "@/features/versioned-editor";
import { formatFullDate } from "@/lib/formatters";

/** What the panel knows about the conversation behind one AI-origin version. */
export interface VersionThreadProvenanceView {
  accessible: boolean;
  title: string | null;
}

export interface VersionOriginPopoverInput {
  /** The human on the version row — the author line never changes for AI-origin versions. */
  authorName: string;
  /** When the version was created, in epoch milliseconds. */
  createdAt: number;
  /** The conversation that produced the version, when one was recorded. */
  threadId: string | null;
  /**
   * The backend's answer for `threadId`, or `undefined` while the query is still in flight
   * or when the id fell outside the provenance scan bound.
   */
  provenance: VersionThreadProvenanceView | undefined;
}

/**
 * What the Claudia mark's popover says.
 *
 * Deliberately a value rather than JSX so the access rule — *a link only when the viewer
 * owns the conversation* — is unit-testable without a DOM, and so "still loading" can never
 * be mistaken for "you may not open this".
 */
export type VersionOriginPopoverContent =
  | { kind: "loading" }
  /** An AI-origin version with no conversation recorded against it. */
  | { kind: "unlinked"; description: string }
  /** Someone else's conversation: named as provenance, never as a link. */
  | { kind: "foreign"; description: string }
  /** The viewer's own conversation. */
  | { kind: "open"; threadId: string; title: string };

const UNTITLED_CHAT_LABEL = "Claudia chat";

export function toPossessive(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "their";
  return /s$/i.test(trimmed) ? `${trimmed}'` : `${trimmed}'s`;
}

export function resolveVersionOriginPopoverContent({
  authorName,
  createdAt,
  threadId,
  provenance,
}: VersionOriginPopoverInput): VersionOriginPopoverContent {
  const formattedDate = formatFullDate(createdAt);

  if (!threadId) {
    // The origin is honest even without a conversation to point at: the change came from
    // Claudia, and there is simply nothing to open.
    return { kind: "unlinked", description: `Created by Claudia on ${formattedDate}` };
  }

  if (provenance === undefined) {
    return { kind: "loading" };
  }

  if (!provenance.accessible) {
    return {
      kind: "foreign",
      description: `Created in ${toPossessive(authorName)} chat on ${formattedDate}`,
    };
  }

  return { kind: "open", threadId, title: provenance.title ?? UNTITLED_CHAT_LABEL };
}

export interface VersionOriginBadgeProps {
  originKind: string | null | undefined;
  originAssistantThreadId: string | null;
  authorName: string;
  createdAt: number;
  provenance: VersionThreadProvenanceView | undefined;
  onOpenChat: (threadId: string) => void;
}

/**
 * The Claudia mark on a version created by the assistant.
 *
 * Renders nothing at all for every other origin, so a manual save's author line carries no mark.
 */
export function VersionOriginBadge({
  originKind,
  originAssistantThreadId,
  authorName,
  createdAt,
  provenance,
  onOpenChat,
}: VersionOriginBadgeProps) {
  if (originKind !== "ai") {
    return null;
  }

  const content = resolveVersionOriginPopoverContent({
    authorName,
    createdAt,
    threadId: originAssistantThreadId,
    provenance,
  });

  return (
    <VersionRowBadge
      icon={<ClaudiaIcon className="size-3" aria-hidden="true" />}
      ariaLabel="Created by Claudia — show chat details"
      testId="version-origin-badge"
      popoverTestId="version-origin-popover"
    >
      <div className="flex items-center gap-1.5">
        <ClaudiaIcon className="size-3.5" aria-hidden="true" />
        <span className="text-xs font-medium">Created by Claudia</span>
      </div>

      {content.kind === "loading" && (
        <p className="text-[11px] text-muted-foreground">Loading chat details…</p>
      )}

      {(content.kind === "unlinked" || content.kind === "foreign") && (
        <p className="text-[11px] text-muted-foreground">{content.description}</p>
      )}

      {content.kind === "open" && (
        <div className="flex flex-col items-start gap-1.5">
          <p className="text-[11px] text-foreground">{content.title}</p>
          <button
            type="button"
            className="text-[11px] font-medium text-primary underline-offset-2 hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              onOpenChat(content.threadId);
            }}
            data-testid="version-origin-open-chat"
          >
            Open chat
          </button>
        </div>
      )}
    </VersionRowBadge>
  );
}
